import {
  ChangeGroup,
  InlineEditChangeGroupEntry,
  ChangeVisual,
  Change,
  InlineEditChangeFocusEventPayload,
} from '@amzn/awscat-editor';
import { InlineEditItem, InlineEditResolution } from '@amzn/awscat-pattern-library-service-model';
import _ from 'lodash';
import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useInlineEditsManager } from '../../../contexts/InlineEditsManagerContext';
import { useSuggestionNavigationService } from '../../../contexts/SuggestionNavigationServiceContext';
import { docIdEquals } from '../../../model/RichtextDocId';
import { InlineEditsManager } from '../../../services/InlineEditsManager';
import { sanitizeInlineEditReplacement } from '../../../services/InlineEditSanitizer';
import { InlineTextFilter } from '../../../services/InlineTextOperations';
import { SectionEditChanges } from '../../../services/SuggestionNavigationService';
import { useFilteredInlineEdits } from '../../v2-hooks/useFilteredInlineEdits';
import { scrollIntoComfortZone } from './Window';
import { Editor } from '@tiptap/core';
import { log } from '../../../loggers/Logger';
import FlashbarMessageBroker from '../../../common/FlashbarMessageBroker';
import { CHANGES_MODIFIED_THROTTLE, DEFAULT_INLINE_TEXT_FILTER, PopoverState } from './Constants';
import { useStableDocId } from './useStableDocId';

/**
 * Maps the InlineEditItem received from the AI agents to ChangeGroup for
 * displaying in the Tiptap editor
 */
function toChangeGroups(editor: Editor, inlineEdits: readonly InlineEditItem[]): ChangeGroup[] {
  return inlineEdits
    .filter((edit) => !edit.resolved)
    .reduce<ChangeGroup[]>((groups, edit) => {
      try {
        // Sanitize agent output before deserialization — nodeFromJSON validates
        // node/mark types but never attribute values, so a hostile edit is
        // rejected here rather than rendered/persisted.
        groups.push({
          id: edit.editId,
          nodeId: edit.nodeId,
          replacementNode: editor.schema.nodeFromJSON(
            sanitizeInlineEditReplacement(edit.replacement),
          ),
        });
      } catch (e) {
        log.warn({ err: e }, `[toChangeGroups] Rejected inline edit ${edit.editId}:`);
      }
      return groups;
    }, []);
}

function areSetEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
}

function toNavigationChanges(
  inlineEdits: readonly InlineEditItem[],
  changesByEdit: ReadonlyMap<string, SectionEditChanges>,
): readonly SectionEditChanges[] {
  return inlineEdits
    .filter((edit) => !edit.resolved)
    .map(
      (edit) =>
        changesByEdit.get(edit.editId) ?? {
          inlineEditId: edit.editId,
          changes: [],
        },
    );
}

/** Resolves inline edits whose changes have been consumed by user interaction. */
export async function resolveModifiedEdits(
  entries: readonly InlineEditChangeGroupEntry[],
  manager: InlineEditsManager,
): Promise<void> {
  const opened = manager.openedEdits;
  if (!opened) return;

  // Collect resolution attempts so a whole pass reports at most one notice.
  const resolutions: Promise<void>[] = [];
  for (const entry of entries) {
    // When changes are missing compared to the original, the user acted on this group.
    // When there were no changes to begin with, the text already matches the edit,
    // so the user could never accept or reject it in the editor.
    const isNoDiff = entry.originalChanges.length === 0;
    if (!isNoDiff && entry.changes.length >= entry.originalChanges.length) continue;

    const editId = entry.changeGroup.id;
    const edit = opened.edits.find((e) => e.editId === editId);
    if (edit?.resolved) continue;

    // The user interacted with the inline edit, implicitly accepting it by
    // manually typing the resolution rather than using the accept/reject buttons.
    resolutions.push(
      manager.tryResolveEdit(editId, InlineEditResolution.ACCEPTED).catch((error) => {
        log.error({ err: error }, `[useInlineEditHandler] Failed to resolve edit "${editId}"`);
        throw error;
      }),
    );
  }

  // A .catch fires asynchronously after this loop, so we await before deciding
  // whether to notify — a single notice covers however many failed this pass.
  const results = await Promise.allSettled(resolutions);
  if (results.some((r) => r.status === 'rejected')) {
    // Implicit (typed-over) resolution — surface a low-severity, non-blocking notice.
    FlashbarMessageBroker.create().pushWarnMessage(
      "Your edit was applied, but we couldn't mark the suggestion resolved. It may reappear until you retry.",
    );
  }
}

/**
 * Skips an insert that immediately follows a delete ending at the same position.
 * The delete represents the logical navigation target; the trailing insert is
 * just the replacement text occupying the same location. Only the first insert
 * after a delete is skipped; subsequent inserts are kept.
 */
export function collapseReplacementPairs(
  changes: readonly ChangeVisual[],
): readonly ChangeVisual[] {
  const result: ChangeVisual[] = [];

  for (let i = 0; i < changes.length; i++) {
    const current = changes[i];
    if (!current) continue;

    const prev = changes[i - 1];

    // Skip the insert when it immediately follows a delete ending at the same position.
    if (current.type === 'insert' && prev?.type === 'delete' && current.fromPos === prev.toPos) {
      continue;
    }

    result.push(current);
  }

  return result;
}

/**
 * Resolves the navigation target when a user clicks a change inside the editor.
 * Insertions are grouped with their preceding deletion for navigation purposes,
 * so clicking an insertion should land on the deletion — but only when the
 * immediately preceding change is in fact a deletion.
 */
export function resolveClickTarget(changeId: number, changes: readonly Change[]): number {
  const clickedIndex = changes.findIndex((c) => c.id === changeId);
  const clickedChange = clickedIndex >= 0 ? changes[clickedIndex] : undefined;
  const preceding = clickedIndex > 0 ? changes[clickedIndex - 1] : undefined;

  if (clickedChange?.type === 'insert' && preceding?.type === 'delete') {
    return preceding.id;
  }

  return changeId;
}

export interface InlineEditIntegration {
  readonly syncModifiedChanges: (entries: readonly InlineEditChangeGroupEntry[]) => void;
  readonly focusChange: (payload: InlineEditChangeFocusEventPayload) => void;
  readonly popover: PopoverState | undefined;
  readonly hidePopover: () => void;
}

export function useInlineEditIntegration(
  editor: Editor | undefined,
  inlineTextFilter: InlineTextFilter | undefined,
): InlineEditIntegration {
  // Preps
  const [popover, setPopover] = useState<PopoverState | undefined>(undefined);
  const documentId = useStableDocId(inlineTextFilter);
  const inlineEditManager = useInlineEditsManager();
  const navigationService = useSuggestionNavigationService();
  const sentInlineEditIds = useRef<Set<string>>(new Set());
  // Optimization: create a Popover ref so Popover state can be accessed
  // from inside useCallback, without this, when popover state changes,
  // the callback will change and causes Tiptap to re-render
  const popoverRef = useRef<PopoverState | undefined>(undefined);
  popoverRef.current = popover;
  // Guards against the re-entry cycle: forceClickChange → focusChange → moveInto → forceClickChange.
  const isProgrammaticClickRef = useRef(false);

  // Retrieve inline edits
  const inlineEdits = useFilteredInlineEdits(inlineTextFilter ?? DEFAULT_INLINE_TEXT_FILTER);

  // When the editor is ready, send the inline edits to it via
  // the setChangeGroups() command
  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      // Editor is destroyed, clear the prevIdsRef,
      // Otherwise when it enabled again, the areSetEqual() below will prevent ids
      // to be re-sent to Tiptap.
      sentInlineEditIds.current = new Set();
      return;
    }

    if (!inlineEdits?.length) {
      if (sentInlineEditIds.current.size === 0) return;
      sentInlineEditIds.current = new Set();
      editor.commands.setChangeGroups([]);
      return;
    }

    // Only re-sync when edits are added or removed — resolution changes don't need
    // a re-sync because the editor already reflects user interactions that caused them.
    const nextIds = new Set(inlineEdits.filter((e) => !e.resolved).map((e) => e.editId));
    if (areSetEqual(sentInlineEditIds.current, nextIds)) {
      return;
    }

    sentInlineEditIds.current = nextIds;
    const changeGroups = toChangeGroups(editor, inlineEdits);
    editor.commands.setChangeGroups(changeGroups);
  }, [editor, inlineEdits]);

  // Accumulated changes per edit group, keyed by editId.
  // The callback only receives groups that changed, so we accumulate to report the full picture.
  const navigatableChangesPerInlineEdit = useRef<Map<string, SectionEditChanges>>(new Map());

  // Clear this document's navigation report when the editor unmounts.
  useEffect(() => {
    return () => {
      if (documentId) {
        navigationService.clear({ type: 'doc_inline_edit', documentId });
      }
    };
  }, [navigationService, documentId]);

  // Register edit ownership immediately; TipTap fills in clickable change geometry afterward.
  useEffect(() => {
    if (!documentId) return;
    navigationService.report({
      type: 'doc_inline_edit',
      documentId,
      changes: toNavigationChanges(inlineEdits, navigatableChangesPerInlineEdit.current),
    });
  }, [navigationService, documentId, inlineEdits]);

  // When the editor generates the inline edit changes (the diff)
  // Report it to NavigationService so user can navigate here
  const syncModifiedChanges = useCallback(
    (entries: readonly InlineEditChangeGroupEntry[]) => {
      void resolveModifiedEdits(entries, inlineEditManager);

      // Update tracked changes from entries and re-report to navigation.
      for (const entry of entries) {
        navigatableChangesPerInlineEdit.current.set(entry.changeGroup.id, {
          inlineEditId: entry.changeGroup.id,
          changes: collapseReplacementPairs(entry.changes),
        });
      }

      if (documentId) {
        navigationService.report({
          type: 'doc_inline_edit',
          documentId,
          changes: toNavigationChanges(inlineEdits, navigatableChangesPerInlineEdit.current),
        });
      }
    },
    [inlineEditManager, navigationService, documentId, inlineEdits],
  );
  const syncModifiedChangesThrottled = useMemo(
    () =>
      _.throttle(syncModifiedChanges, CHANGES_MODIFIED_THROTTLE, {
        leading: false,
        trailing: true,
      }),
    [syncModifiedChanges],
  );

  // When unmount, cancel pending throttled calls.
  useEffect(() => {
    return () => syncModifiedChangesThrottled.cancel();
  }, [syncModifiedChangesThrottled]);

  // When Tiptap the user clicks on the changes inside tiptap editor, or when the
  // "Change" is moved, handle that..
  const focusChange = useCallback(
    (payload: InlineEditChangeFocusEventPayload) => {
      if (payload.type === 'click') {
        const targetChangeId = resolveClickTarget(payload.changeId, payload.changes);

        // When the click was triggered programmatically by forceClickChange (navigation-driven),
        // skip moveInto to break the re-entry cycle.
        if (isProgrammaticClickRef.current) {
          isProgrammaticClickRef.current = false;
        } else {
          navigationService.moveInto({
            type: 'doc_inline_edit',
            inlineEditId: payload.changeGroup.id,
            changeId: targetChangeId,
          });
        }

        // Open the popover with fresh DOM position from the click payload.
        const changesInGroup = navigatableChangesPerInlineEdit.current?.get(
          payload.changeGroup.id,
        )?.changes;
        const targetChange = payload.changes.find((c) => c.id === targetChangeId);
        if (targetChange) {
          setPopover({
            type: 'inline_edit',
            target: targetChange.domRect,
            inlineEditId: payload.changeGroup.id,
            changeId: targetChangeId,
            shouldResolveInlineEdit: changesInGroup?.length === 1,
          });
          scrollIntoComfortZone(targetChange.domRect);
        }
      }

      // When the editor reports updated positions and the popover is showing
      // for the same change group, update the popover target position.
      if (payload.type === 'update') {
        const current = popoverRef.current;
        if (!current || current.type !== 'inline_edit') return;
        if (current.inlineEditId !== payload.changeGroup.id) return;

        const updatedChange = payload.changes.find((c) => c.id === current.changeId);
        if (updatedChange) {
          setPopover({ ...current, target: updatedChange.domRect });
        }
      }
    },
    [navigationService, navigatableChangesPerInlineEdit],
  );

  // When navigation moves to an inline edit, trigger a click on the change via the editor command.
  useEffect(() => {
    if (!documentId) {
      return;
    }
    const unsub = navigationService.on('navigationItemChanged', ({ item }) => {
      // Dismiss popover when navigation moves to a different document.
      if (item.type !== 'doc_inline_edit' || !docIdEquals(item.documentId, documentId)) {
        setPopover(undefined);
        return;
      }

      // Skip if the popover is already showing this exact change.
      const current = popoverRef.current;
      if (
        current?.type === 'inline_edit' &&
        current.inlineEditId === item.inlineEditId &&
        current.changeId === item.change.id
      ) {
        return;
      }

      // Programmatically focus the change in the editor.
      if (editor) {
        isProgrammaticClickRef.current = true;
        editor.commands.forceClickChange(item.inlineEditId, item.change.id);
      }
    });
    return () => {
      unsub();
    };
  }, [navigationService, editor, documentId]);

  const hidePopover = useCallback(() => {
    setPopover(undefined);
  }, []);

  return {
    syncModifiedChanges: syncModifiedChangesThrottled,
    focusChange,
    popover,
    hidePopover,
  };
}
